import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OrderedMap } from 'immutable';
import { Alert } from '@cmsgov/ds-healthcare-gov';
import IncomeContainer from './IncomeContainer';
import ExpenseContainer from './ExpenseContainer';
import HouseholdMemberInfo from './HouseholdMemberInfo';
import NetExpectedIncome from './NetExpectedIncome';
import NextSteps from './NextSteps';
import buildPdf from '../../utilities/buildPdf';
import { sendEvent, getEnvironment } from '../../utilities/analytics';

import type { FrequencyType } from '../../common/enums/FrequencyTypes';
import type { IncomeType } from '../../common/enums/IncomeTypes';
import { DateObject } from './IncomeFields/IncomeFields';
import { ExpenseType } from '../../common/enums/ExpenseTypes';

export type Income = {
  amount: string;
  calculatedTotal: number;
  incomeFrequency: FrequencyType;
  incomeType: IncomeType;
  startDate: DateObject;
  endDate: DateObject;
  id?: string;
};

export type Expense = {
  expenseType: ExpenseType;
  amount: string;
  expenseFrequency: FrequencyType;
  calculatedTotal: number;
};

const IncomeCalculator = () => {
  const { t } = useTranslation();

  const [name, setName] = useState<string | null>(null);
  const [incomeTotal, setIncomeTotal] = useState(0);
  const [expensesTotal, setExpensesTotal] = useState(0);
  const [incomes, setIncomes] = useState<OrderedMap<string, Income>>(OrderedMap());
  const [expenses, setExpenses] = useState<OrderedMap<string, Expense>>(OrderedMap());
  const [key, setKey] = useState(0);

  useEffect(() => {
    if (globalThis.utag_data) {
      globalThis.utag_data.page_name = 'app 3.0: estimate income';
      globalThis.utag_data.content_language = globalThis.location.hostname.includes('cuidadodesalud.gov') ? 'es' : 'en';
      globalThis.utag_data.content_type = 'estimate income page';
      globalThis.utag_data.site_division = 'apply';
      globalThis.utag_data.site_domain = globalThis.location.hostname;
      globalThis.utag_data.site_environment = getEnvironment();
      globalThis.utag_data.site_section = 'application - unauthenticated tool';
      globalThis.utag_data.page_type = 'tool';
      globalThis.utag_data.page_topic = 'income';
      globalThis.utag_data.page_lifecycle = 'education';
      globalThis.utag_data.page_audience = 'individual';
      globalThis.utag_data.page_category = 'income-and-household-information';
    }
  }, []);

  useEffect(() => {
    const newIncomeTotal = incomes.reduce((accum, current) => {
      return accum + current.calculatedTotal;
    }, 0);
    setIncomeTotal(newIncomeTotal);
  }, [incomes]);

  useEffect(() => {
    const newExpensesTotal = expenses.reduce((accum, current) => {
      return accum + current.calculatedTotal;
    }, 0);
    setExpensesTotal(newExpensesTotal);
  }, [expenses]);

  // Member Name
  const handleUpdateHouseholdMemberName = (name: string) => {
    setName(name);
  };

  // Income
  const handleAddNewIncome = (incomeRowState: Income) => {
    const id = `income-${crypto.randomUUID()}`;
    setIncomes(incomes.set(id, { ...incomeRowState, id }));
  };

  const handleRemoveIncome = (id: string) => {
    setIncomes(incomes.delete(id));
  };

  const handleUpdateIncomeEntry = (incomeState: Income, id: string) => {
    setIncomes(incomes.set(id, { ...incomeState, id }));
  };

  // Expenses
  const handleAddNewExpense = (expenseRowState: Expense) => {
    const id = `expense-${crypto.randomUUID()}`;
    handleUpdateExpenseEntry(expenseRowState, id);
  };

  const handleRemoveExpense = (id: string) => {
    setExpenses(expenses.delete(id));
  };

  const handleUpdateExpenseEntry = (expenseState: Expense, id: string) => {
    setExpenses(expenses.set(id, { ...expenseState }));
  };

  // Clear form
  const handleClearForm = () => {
    sendEvent('estimate income', 'cleared form', 'unauthenticated', 'clear_form');

    setName(null);
    setIncomeTotal(0);
    setExpensesTotal(0);
    setIncomes(incomes.clear());
    setExpenses(expenses.clear());
    setKey(key + 1);
  };

  const handleBuildPDF = () => {
    sendEvent('download', 'pdf', 'income-estimate.pdf', 'file_download');
    buildPdf(t, name, incomeTotal, expensesTotal, incomes, expenses);
  };

  return (
    <main id="main-body-content" className="ds-l-container ds-u-margin-bottom--4" tabIndex={-1}>
      <div className="ds-l-row">
        <div className="ds-l-col--12">
          <h1 className="ds-text-heading--4xl ds-u-margin-top--4">{t('estimatorPageTitle')}</h1>
          <div className="ds-u-font-size--xl ds-u-margin-top--2 ds-u-margin-bottom--1">{t('estimatorLead')}</div>
        </div>
      </div>
      <div className="ds-l-row">
        <div className="ds-l-col--12 ds-l-md-col--8">
          <Alert className="ds-u-margin-top--3 ds-u-margin-bottom--2" heading={t('estimatorAlertTitle')}>
            {t('estimatorAlertBody')}
          </Alert>
        </div>
      </div>
      <div className="ds-l-row">
        <div className="ds-l-col--12">
          <HouseholdMemberInfo name={name} updateHouseholdMemberName={handleUpdateHouseholdMemberName} />
          <IncomeContainer
            incomes={incomes}
            incomeTotal={incomeTotal}
            onAddNewIncome={handleAddNewIncome}
            handleUpdateIncomeEntry={handleUpdateIncomeEntry}
            handleRemoveIncomeEntry={handleRemoveIncome}
            key={`income-${key}`}
          />
          <ExpenseContainer
            onAddNewExpense={handleAddNewExpense}
            expensesTotal={expensesTotal}
            expenses={expenses}
            handleUpdateExpenseEntry={handleUpdateExpenseEntry}
            handleRemoveExpenseEntry={handleRemoveExpense}
            key={`expense-${key}`}
          />
          <NetExpectedIncome netTotal={incomeTotal - expensesTotal} />
          <NextSteps clearForm={handleClearForm} buildPDF={handleBuildPDF} />
        </div>
      </div>
    </main>
  );
};

export default IncomeCalculator;
